import React from 'react';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';

const TermsOfService = () => {
  return (
    <>
      <SEO 
        title="Terms of Service | Paracca Financial Group"
        description="Terms of Service for Paracca Financial Group - Understanding the terms and conditions of our advisory services."
      />
      
      <div className="bg-white">
        {/* Hero Section */}
        <section className="bg-[#0a69b2] text-white py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="font-bellefair text-4xl sm:text-5xl uppercase mb-4">Terms of Service</h1>
            <p className="font-montserrat text-base text-gray-100">
              Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose prose-lg max-w-none font-montserrat">
              
              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Agreement to Terms</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Welcome to Paracca Financial Group. By accessing our website or engaging our investment advisory services, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using or accessing our services.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Use of Website</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  This website is provided for informational purposes only. You may use our website for lawful purposes and in accordance with these Terms. You agree not to:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Use the website in any way that violates any applicable federal, state, or local law or regulation</li>
                  <li>Attempt to gain unauthorized access to any portion of the website or any systems or networks</li>
                  <li>Engage in any conduct that restricts or inhibits anyone's use or enjoyment of the website</li>
                  <li>Use any robot, spider, or other automatic device to access the website</li>
                  <li>Introduce any viruses, trojan horses, worms, or other harmful material</li>
                  <li>Copy, modify, or distribute any content from the website without our prior written consent</li>
                </ul>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Investment Advisory Services</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Paracca Financial Group is a registered investment adviser. Our investment advisory services are provided pursuant to a written Investment Advisory Agreement. The terms of that agreement govern the advisory relationship and may differ from or supplement these Terms of Service.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>Important Information:</strong>
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>We act as a fiduciary to our advisory clients, putting their interests first</li>
                  <li>All investment advisory services are provided based on your individual circumstances, goals, and risk tolerance</li>
                  <li>Past performance is not indicative of future results</li>
                  <li>All investments involve risk, including possible loss of principal</li>
                  <li>No guarantee or representation is made that our services will achieve any particular result</li>
                </ul>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">No Investment Advice on Website</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The information provided on this website is for general informational and educational purposes only. Nothing on this website constitutes investment advice, financial advice, trading advice, or any other sort of advice. You should not treat any of the website's content as a recommendation or inducement to buy or sell any security or other investment.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Before making any investment decision, you should seek appropriate professional advice and carefully consider your financial situation, investment objectives, risk tolerance, and other relevant circumstances.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Fees and Compensation</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Our fee structure for investment advisory services is detailed in our Form ADV Part 2A (our firm brochure) and in your Investment Advisory Agreement. Fees are negotiable and may vary based on the size and complexity of your account. We encourage you to review our <Link to="/disclosures" className="text-[#0a69b2] hover:underline">Disclosures</Link> page and Form ADV for complete information about our fees and compensation arrangements.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Intellectual Property Rights</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The content on this website, including but not limited to text, graphics, logos, images, and software, is the property of Paracca Financial Group or its content suppliers and is protected by United States and international copyright laws. You may not reproduce, distribute, modify, create derivative works of, publicly display, or publicly perform any content from our website without our express written permission.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Third-Party Links and Resources</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Our website may contain links to third-party websites, resources, or services that are not owned or controlled by Paracca Financial Group. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party websites or services. You acknowledge and agree that we shall not be responsible or liable for any damage or loss caused by your use of any such content, goods, or services available through third-party websites.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Disclaimer of Warranties</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  This website and its content are provided "as is" and "as available" without any warranties of any kind, either express or implied. We do not warrant that:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>The website will be available at all times or be uninterrupted</li>
                  <li>The website will be free from errors, viruses, or other harmful components</li>
                  <li>The information on the website is accurate, complete, or current</li>
                  <li>Any defects will be corrected</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Your use of the website is at your own risk.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Limitation of Liability</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  To the fullest extent permitted by applicable law, Paracca Financial Group, its officers, directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or other intangible losses, resulting from:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Your use or inability to use the website</li>
                  <li>Any unauthorized access to or use of our servers or any personal information stored therein</li>
                  <li>Any interruption or cessation of transmission to or from the website</li>
                  <li>Any bugs, viruses, or other harmful code transmitted through the website</li>
                  <li>Any errors or omissions in any content</li>
                </ul>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Indemnification</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  You agree to defend, indemnify, and hold harmless Paracca Financial Group and its officers, directors, employees, and agents from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or relating to your violation of these Terms or your use of the website.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Governing Law and Dispute Resolution</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  These Terms shall be governed by and construed in accordance with the laws of the State of Florida, without regard to its conflict of law provisions. Any disputes arising out of or relating to these Terms or your use of the website shall be resolved through binding arbitration in accordance with the rules of FINRA or the American Arbitration Association, unless otherwise specified in your Investment Advisory Agreement.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Changes to Terms</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We reserve the right to modify or replace these Terms at any time at our sole discretion. We will provide notice of material changes by posting the updated Terms on this website with a revised "Last Updated" date. Your continued use of the website after such changes constitutes acceptance of the new Terms.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Severability</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall continue in full force and effect. The invalid or unenforceable provision shall be deemed modified to the extent necessary to make it valid and enforceable.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Entire Agreement</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  These Terms, together with our Privacy Policy and any Investment Advisory Agreement, constitute the entire agreement between you and Paracca Financial Group regarding your use of the website and supersede all prior agreements and understandings.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Contact Information</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If you have any questions about these Terms of Service, please contact us:
                </p>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 mb-2"><strong>Paracca Financial Group</strong></p>
                  <p className="text-gray-700 mb-2">428 S Tamiami Trail, Osprey, FL 34229</p>
                  <p className="text-gray-700 mb-2">Phone: <a href="tel:941-545-9057" className="text-[#0a69b2] hover:underline">941-545-9057</a></p>
                  <p className="text-gray-700">Email: <a href="mailto:contact@paracca.com" className="text-[#0a69b2] hover:underline">contact@paracca.com</a></p>
                </div>
              </div>

            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default TermsOfService;
